import React from "react";

// ─── Styles ───────────────────────────────────────────────────────────────────
export const KBD = { background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: "3px", padding: "1px 5px", fontSize: "10px", fontFamily: "monospace", color: "#374151" };
export const TH_NUM = { width: "40px", minWidth: "40px", background: "#cbd5e1", border: "1px solid #adb5bd", textAlign: "center", fontSize: "11px", color: "#64748b", height: "28px", position: "sticky", top: 0, zIndex: 10, boxSizing: "border-box" };
export const thCol  = (w, inColSel) => ({ minWidth: `${w}px`, width: `${w}px`, background: inColSel ? "#1b5e20" : "#217346", color: "#fff", border: "1px solid #1b5e20", padding: "0 6px", fontSize: "12px", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", height: "28px", position: "sticky", top: 0, zIndex: 5, boxSizing: "border-box", userSelect: "none" });
export const rnTd   = (ri, focR) => ({ width: "40px", minWidth: "40px", background: focR === ri ? "#dbeafe" : "#f1f5f9", border: "1px solid #d0d0d0", textAlign: "center", fontSize: "11px", color: "#64748b", height: "28px", fontWeight: focR === ri ? 700 : 400, padding: 0, position: "sticky", left: 0, zIndex: 2, userSelect: "none" });
export const tdStyle = (isFoc, isSel, ri, isEditFoc, isRO) => ({ height: "28px", padding: isEditFoc ? 0 : "0 3px", border: isFoc ? "2px solid #1565c0" : isSel ? "1px solid #90caf9" : "1px solid #d0d0d0", background: isRO ? (isFoc ? "#e3f2fd" : isSel ? "#bbdefb" : ri % 2 === 0 ? "#f5f5f5" : "#efefef") : (isFoc ? "#ffffff" : isSel ? "#bbdefb" : ri % 2 === 0 ? "#ffffff" : "#f8f9fa"), cursor: isRO ? "default" : "cell", position: "relative", zIndex: isFoc ? 4 : isSel ? 2 : 1, boxSizing: "border-box", overflow: "hidden", verticalAlign: "middle" });

export const getSelRange = (foc, anc) => ({
  r1: Math.min(foc.r, anc.r), r2: Math.max(foc.r, anc.r),
  c1: Math.min(foc.c, anc.c), c2: Math.max(foc.c, anc.c),
});

export const colLetter = (c) => {
  let s = ""; let ni = c + 1;
  while (ni > 0) { s = String.fromCharCode(64 + ((ni - 1) % 26 + 1)) + s; ni = Math.floor((ni - 1) / 26); }
  return s;
};

// ─── Memoized GridRow for instant navigation performance ──────────────────────
export const GridRow = React.memo(({
  row,
  ri,
  visCOLS,
  isFocused,
  focusedCol,
  editMode,
  isSelected,
  selStartCol,
  selEndCol,
  isHovered,
  isColumnReadOnly,
  handleCellClick,
  enterEditMode,
  setCell,
  delRow,
  getAllowanceBreakdown,
  getDeductionBreakdown,
  employees,
  inputRef,
  handleKey,
  doPaste,
  gotoCell,
  editValue,
  setLocalEditValue,
  editValueRef,
  editModeRef,
  focusedCellRef,
  setHoverRowIdx,
  setEditMode,
  displayVal
}) => {
  const isSavedRow = row._isSaved;
  const isNewEmpRow = row._isNew;

  return (
    <tr>
      <td style={{ ...rnTd(ri, isFocused ? ri : -1), background: isSavedRow ? "#e8f5e9" : isNewEmpRow ? "#f5f5f5" : rnTd(ri, isFocused ? ri : -1).background }}>{ri + 1}</td>
      {visCOLS.map((col, ci) => {
        const cellFocused  = isFocused && focusedCol === ci;
        const cellSelected = isSelected && ci >= selStartCol && ci <= selEndCol;
        const isRO         = isColumnReadOnly(col);
        
        return (
          <td
            key={ci}
            ref={cellFocused ? focusedCellRef : null}
            style={{ 
              ...tdStyle(cellFocused, cellSelected, ri, cellFocused && editMode && !isRO, isRO), 
              background: isSavedRow 
                ? (cellFocused ? "#c8e6c9" : cellSelected ? "#dcedc8" : "#e8f5e9")
                : isNewEmpRow
                ? (cellFocused ? "#f0f0f0" : cellSelected ? "#f5f5f5" : "#fafafa")
                : (tdStyle(cellFocused, cellSelected, ri, cellFocused && editMode && !isRO, isRO).background),
              minWidth: `${col.w}px`, 
              width: `${col.w}px`,
              position: "relative"
            }}
            onMouseDown={e => handleCellClick(ri, ci, e)}
            onDoubleClick={() => !isRO && enterEditMode(ri, ci, null)}
            onMouseEnter={() => (col.key === "TotalAllowances" || col.key === "TotalDeductions") && setHoverRowIdx(ri)}
            onMouseLeave={() => setHoverRowIdx(null)}
          >
            {cellFocused && editMode && !isRO
              ? (() => {
                  const val = row[col.key] ?? "";
                  const shared = {
                    ref: inputRef,
                    style: { width: "100%", height: "100%", border: "none", outline: "none", background: "transparent", padding: "0 4px", fontSize: "13px", boxSizing: "border-box" },
                    onKeyDown: handleKey,
                    onPaste: (e) => { const text = e.clipboardData?.getData("text"); if (text && (text.includes("\t") || text.includes("\n"))) { e.preventDefault(); doPaste(text, ri, ci); } },
                  };
                  if (col.type === "select" && col.optKey === "EmployeeMaster") {
                    return (
                      <select {...shared} value={val} onChange={e => { setCell(ri, col.key, e.target.value); setEditMode(false); ci === visCOLS.length - 1 ? gotoCell(ri + 1, 0) : gotoCell(ri, ci + 1); }}>
                        <option value="">-- Select Employee --</option>
                        {employees.map(emp => <option key={emp.Id} value={emp.Id}>{emp.Name}</option>)}
                      </select>
                    );
                  }
                  return (
                    <input
                      {...shared}
                      type={col.type === "number" ? "number" : col.type === "date" ? "date" : "text"}
                      value={editValue}
                      onChange={e => setLocalEditValue(e.target.value)}
                      onBlur={() => {
                        if (editModeRef.current) {
                          setCell(ri, col.key, editValueRef.current);
                          setEditMode(false);
                        }
                      }}
                    />
                  );
                })()
              : (() => {
                  const dv = displayVal(col, row[col.key]);
                  const isNum = col.type === "number";
                  const isEmpSel = col.type === "select" && !dv;
                  
                  // Show breakdown tooltip for allowances/deductions
                  let tooltip = null;
                  if (isHovered) {
                    if (col.key === "TotalAllowances") {
                      const breakdown = getAllowanceBreakdown(ri);
                      if (breakdown.length > 0) {
                        tooltip = breakdown.map(b => `${b.name}: ${b.amount.toFixed(2)}`).join("\n");
                      }
                    } else if (col.key === "TotalDeductions") {
                      const breakdown = getDeductionBreakdown(ri);
                      if (breakdown.length > 0) {
                        tooltip = breakdown.map(b => `${b.name}: ${b.amount.toFixed(2)}`).join("\n");
                      }
                    }
                  }
                  
                  return (
                    <>
                      <span style={{
                        display: "block", padding: "0 4px", overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: "26px",
                        textAlign: isNum ? "right" : "left",
                        color: isEmpSel ? "#b0b7c3" : isRO ? "#1a5276" : (row[col.key] !== "" && row[col.key] !== 0) ? "#212529" : "#bbb",
                        fontWeight: (isRO && (col.key === "NetAmount" || col.key === "GrossAmount")) ? 700 : cellFocused ? 600 : 400,
                        fontStyle: isEmpSel ? "italic" : "normal",
                        background: col.key === "NetAmount" && row[col.key] > 0 ? "rgba(100,200,100,0.12)" : "transparent",
                        cursor: (col.key === "TotalAllowances" || col.key === "TotalDeductions") ? "help" : "default"
                      }}>
                        {isEmpSel ? "Select..." : isNum && dv !== "" ? (Number(dv) !== 0 ? Number(dv).toFixed(2) : "") : (dv || "\u00A0")}
                      </span>
                      {tooltip && (
                        <div style={{
                          position: "absolute", bottom: "100%", left: 0, right: 0,
                          background: "#2c3e50", color: "#fff", padding: "8px", fontSize: "11px",
                          borderRadius: "4px", whiteSpace: "pre-wrap", wordBreak: "break-word",
                          zIndex: 9999, boxShadow: "0 2px 8px rgba(0,0,0,0.2)", marginBottom: "4px",
                          border: "1px solid #1a5276", maxWidth: "250px"
                        }}>
                          {tooltip}
                        </div>
                      )}
                    </>
                  );
                })()
            }
          </td>
        );
      })}
      <td style={{ width: "36px", minWidth: "36px", height: "28px", border: "1px solid #d0d0d0", textAlign: "center", background: "#fafafa", padding: 0 }}>
        <button onClick={() => delRow(ri)} title="Delete row" style={{ border: "none", background: "none", color: "#dc3545", fontSize: "18px", lineHeight: 1, cursor: "pointer", width: "100%", height: "100%", fontWeight: 700 }}>×</button>
      </td>
    </tr>
  );
}, (prev, next) => {
  if (prev.row !== next.row) return false;
  if (prev.ri !== next.ri) return false;
  if (prev.isFocused !== next.isFocused) return false;
  if (next.isFocused && prev.focusedCol !== next.focusedCol) return false;
  if (prev.editMode !== next.editMode) return false;
  if (prev.isSelected !== next.isSelected) return false;
  if (next.isSelected && (prev.selStartCol !== next.selStartCol || prev.selEndCol !== next.selEndCol)) return false;
  if (prev.isHovered !== next.isHovered) return false;
  if (prev.editValue !== next.editValue) return false;
  if (prev.visCOLS !== next.visCOLS) return false;
  if (prev.employees !== next.employees) return false;
  return true;
});
